import React, { createContext, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import {
  getCartAPI,
  addCartItemAPI,
  addAllCartItemsAPI,
  updateCartItemAPI,
  removeCartItemAPI,
  clearCartAPI,
  clearCartPartnerAPI,
} from "actions/cartActions";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [cartDetailItems, setCartDetailItems] = useState(() => {
    const savedItem = localStorage.getItem("cartDetailItems");
    return savedItem ? JSON.parse(savedItem) : null;
  });

  const [token, setToken] = useState(() => {
    const savedToken = localStorage.getItem("token");
    return savedToken ? savedToken : null;
  });

  const persistCart = (items) => {
    localStorage.setItem("cart", JSON.stringify(items));
  };

  const fetchCart = async () => {
    const res = await getCartAPI();
    if (res?.status) {
      const items = Array.isArray(res.data) ? res.data : [];
      setCart(items);
      persistCart(items);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCart();
    } else {
      setCart([]);
      localStorage.removeItem("cart");
    }
  }, [token]);

  const login = () => {
    setIsLoggedIn(true);
  };

  const adminLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("cart");
    localStorage.removeItem("cartDetailItems");
    setIsLoggedIn(false);
    setCart([]);
    setToken(null);
    window.location.href = "/";
  };

  const addAllToCart = async (products) => {
    const items = products.map((product) => ({
      ...product,
      qty: parseInt(product.qty),
      required_qty:
        parseInt(product.required_qty) || parseInt(product.price.min_order),
    }));
    // Optimistic
    setCart((prev) => {
      const updated = [...prev, ...items];
      persistCart(updated);
      return updated;
    });
    const res = await addAllCartItemsAPI(items);
    if (res?.status && Array.isArray(res.data)) {
      // Reconcile with server rows (carry cart_id, created_at)
      await fetchCart();
    }
  };

  const addToCart = async (product) => {
    // Optimistic
    setCart((prev) => {
      const updated = [...prev, product];
      persistCart(updated);
      return updated;
    });
    const res = await addCartItemAPI(product);
    if (res?.status && res.data) {
      // Replace last optimistic entry with server row (idempotent re-sync)
      await fetchCart();
    }
  };

  const addCartDetailItems = (items) => {
    setCartDetailItems(() => {
      localStorage.setItem("cartDetailItems", JSON.stringify(items));
      return items;
    });
  };

  const removeByPartNumber = (partNumber) => {
    setCart((prev) => {
      const matching = prev.filter((item) => item.part_number === partNumber);
      matching.forEach((item) => {
        if (item.cart_id) removeCartItemAPI(item.cart_id);
      });
      const updated = prev.filter((item) => item.part_number !== partNumber);
      persistCart(updated);
      return updated;
    });
  };

  const removeFromCart = (index) => {
    setCart((prev) => {
      const item = prev[index];
      if (item?.cart_id) removeCartItemAPI(item.cart_id);
      const updated = prev.filter((_, idx) => idx !== index);
      persistCart(updated);
      return updated;
    });
  };

  const updateCart = ({ index, product }) => {
    setCart((prev) => {
      const item = prev[index];
      if (item?.cart_id) updateCartItemAPI(item.cart_id, { ...item, ...product });
      const updated = prev.map((it, idx) =>
        idx === index ? { ...it, ...product } : it,
      );
      persistCart(updated);
      return updated;
    });
  };

  const clearCart = () => {
    clearCartAPI();
    localStorage.removeItem("cart");
    setCart([]);
  };

  const clearCartByPartner = (apiPartner) => {
    clearCartPartnerAPI(apiPartner);
    setCart((prev) => {
      const partner = String(apiPartner || "").toLowerCase();
      const updated = prev.filter(
        (item) => item.api_partner?.toLowerCase() !== partner,
      );
      persistCart(updated);
      return updated;
    });
  };

  const clearCartDetailItems = () => {
    localStorage.removeItem("cartDetailItems");
    setCartDetailItems(null);
  };

  const isInCart = (product) => {
    return cart.some(
      (item) =>
        item.part_number?.toLowerCase() === product.part_number?.toLowerCase() &&
        item.api_partner?.toLowerCase() === product.api_partner?.toLowerCase(),
    );
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        adminLogout,
        cart,
        addToCart,
        addAllToCart,
        updateCart,
        removeFromCart,
        isInCart,
        setCart,
        token,
        clearCart,
        clearCartByPartner,
        setToken,
        addCartDetailItems,
        setCartDetailItems,
        clearCartDetailItems,
        cartDetailItems,
        removeByPartNumber,
        fetchCart,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
